import { html, nothing } from 'lit';
import { keyed } from 'lit/directives/keyed.js';

import type { FlagCode } from '../../../generated/flags';
import { flagSvgPath } from '../../../services/Flags';
import type { DeathNoticePlayer } from '../../UIState';

/**
 * Shared template pieces for the kill and death notices: the player chip, and
 * the score and upgrade lines under it (styles live in `theme/notice.css.ts`).
 */

/** Longest name a capped chip shows before "...". Legacy's value. */
const NAME_CAP = 10;

/** The chip's name, cut to NAME_CAP characters when `capped`. Callers decide
 *  when: legacy capped only a multi-victim streak, so a single name showed in
 *  full; ours also caps on the compact breakpoints, where a full 20-character
 *  name overflows the notice. Counts code points, not UTF-16 units, so an
 *  emoji is never split into a broken glyph. */
export function noticeName(name: string, capped: boolean): string {
  // Code points <= code units, so a short string needs no splitting.
  if (!capped || name.length <= NAME_CAP) return name;
  const chars = Array.from(name);
  return chars.length > NAME_CAP ? chars.slice(0, NAME_CAP).join('') + '...' : name;
}

/** Inline style for a chip's `.flag` — the flag set is data-driven (hundreds
 *  of codes), so this can't be a per-flag CSS class. */
export function noticeFlagStyle(flag: FlagCode): string {
  return `background-image: url('${flagSvgPath(flag)}')`;
}

/** The badge after the name: "bot" for bots, else the level if known. */
export function renderNoticeBadge(player: DeathNoticePlayer) {
  if (player.bot) {
    return html`<span class="level bot">bot</span>`;
  }
  if (player.level !== null) {
    return html`<span class="level">${player.level}</span>`;
  }
  return nothing;
}

/** The lines under the chips: the score change, then any upgrades gained
 *  (legacy's "+1 upgrade"). Each is keyed on its value, so a live update that
 *  changes it pops it again. */
export function renderNoticeTally(scoreChange: number | false, upgradesChange: number) {
  return html`
    ${
      scoreChange === false
        ? nothing
        : keyed(
            scoreChange,
            scoreChange >= 0
              ? html`<br /><span class="positive pop">+${scoreChange}</span>`
              : html`<br /><span class="negative pop">-${Math.abs(scoreChange)}</span>`,
          )
    }
    ${
      upgradesChange > 0
        ? keyed(
            upgradesChange,
            html`<br /><span class="upgrade pop"
                >+${upgradesChange}<span class="bold"> upgrade${upgradesChange > 1 ? 's' : ''}</span></span
              >`,
          )
        : nothing
    }
  `;
}
